import { ChevronUp, Plus, Search } from "lucide-react";

const AV = [
  "linear-gradient(135deg,#8b4dff,#4d7cff)",
  "linear-gradient(135deg,#ec4899,#8b4dff)",
  "linear-gradient(135deg,#06b6d4,#4d7cff)",
  "linear-gradient(135deg,#f59e0b,#ec4899)",
];

function Avatar({ n, i = 0, sm = false }) {
  return (
    <span className={`avatar ${sm ? "avatar-sm" : ""}`} style={{ background: AV[i % AV.length] }}>
      {n}
    </span>
  );
}

function LiveVideo({ className = "" }) {
  return (
    <video
      src="/brand/live-room.mp4"
      autoPlay
      muted
      loop
      playsInline
      aria-label="Tap In live session — WVY WRLD, 88 listeners listening together in real time"
      className={`w-[min(300px,80vw)] ${className}`}
      style={{
        WebkitMaskImage: "url(/brand/live-room-mask.png)",
        maskImage: "url(/brand/live-room-mask.png)",
        WebkitMaskSize: "100% 100%",
        maskSize: "100% 100%",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
      }}
      onCanPlay={(e) => {
        const v = e.currentTarget;
        if (!v.dataset.started) {
          v.dataset.started = "1";
          v.play().catch(() => {});
        }
      }}
      ref={(el) => {
        if (el && window.matchMedia("(prefers-reduced-motion: reduce)").matches) el.pause();
      }}
      data-testid="phone-mockup-live"
    />
  );
}

const QUEUE = [
  ["Midnight City", "M83", "maya"],
  ["Nightcall", "Kavinsky", "dee"],
  ["Dreams", "Fleetwood Mac", "vale"],
  ["Redbone", "Childish Gambino", "nico"],
];

function QueueScreen() {
  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-dusk">Shared queue</p>
          <p className="text-[13px] font-semibold mt-0.5">#late-night-drive</p>
        </div>
        <span className="avatar avatar-sm" style={{ background: AV[0] }}>D</span>
      </div>
      <div className="flex-1 space-y-2 overflow-hidden">
        {QUEUE.map(([title, artist, by], i) => (
          <div key={title} className="flex items-center gap-2.5 rounded-xl border border-grape/15 px-2.5 py-2">
            <span className="text-[10px] font-mono text-dusk w-3">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold truncate">{title}</p>
              <p className="text-[9px] text-dusk truncate">{artist} · added by {by}</p>
            </div>
            <span className="flex items-center gap-0.5 text-[9px] font-mono text-grape-soft">
              <ChevronUp size={11} /> 12
            </span>
          </div>
        ))}
        <div className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-grape/30 px-2.5 py-2 text-[10px] text-dusk">
          <Plus size={11} /> Add a track
        </div>
      </div>
      <div className="h-5">
        <MiniWave />
      </div>
    </>
  );
}

const COVERS = [
  "https://images.unsplash.com/photo-1603910234616-3b5f4a6be2b4?q=80&w=400&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1539375665275-f9de415ef9ac?q=80&w=400&auto=format&fit=crop",
];

function MiniWave() {
  return (
    <div className="waveform static h-full" aria-hidden="true">
      {[0.5, 0.9, 0.4, 0.75, 0.55, 0.95, 0.45, 0.8, 0.6, 0.5, 0.85, 0.4, 0.7, 0.55].map((h, i) => (
        <span key={i} className="wave-bar" style={{ "--h": h }} />
      ))}
    </div>
  );
}

function DiscoverScreen() {
  return (
    <>
      <div className="flex items-center gap-2 rounded-xl border border-grape/20 px-3 py-2 text-[10px] text-dusk">
        <Search size={11} /> Rooms, tracks, people
      </div>
      <div className="flex gap-1.5">
        {["For you", "Live now", "Indie"].map((c, i) => (
          <span
            key={c}
            className={`px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-wider ${
              i === 0 ? "bg-grape/20 text-grape-soft border border-grape/30" : "border border-grape/15 text-dusk"
            }`}
          >
            {c}
          </span>
        ))}
      </div>
      <div className="flex-1 space-y-2.5 overflow-hidden">
        {COVERS.map((src, i) => (
          <div key={src} className="rounded-xl overflow-hidden border border-grape/15">
            <div className="relative h-16">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <span className="absolute top-1.5 right-1.5 live-chip !text-[8px]">Live</span>
            </div>
            <div className="p-2">
              <p className="text-[11px] font-semibold">{i === 0 ? "Rooftop Sunset" : "Vinyl Therapy"}</p>
              <p className="text-[9px] text-dusk">{i === 0 ? "hosted by Maya" : "hosted by Vale"}</p>
              <div className="h-3.5 mt-1.5">
                <MiniWave />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-[#8b4dff] to-[#4d7cff] py-2 text-[10px] font-semibold text-white">
        Tap in
      </div>
    </>
  );
}

export default function PhoneMockup({ variant = "live", className = "" }) {
  if (variant === "live") return <LiveVideo className={className} />;
  return (
    <div className={`phone ${className}`} data-testid={`phone-mockup-${variant}`}>
      <div className="phone-notch" />
      <div className="phone-screen">
        {variant === "queue" && <QueueScreen />}
        {variant === "discover" && <DiscoverScreen />}
      </div>
    </div>
  );
}
